import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Circumvention Attempts — admin list of flagged off-platform
// circumvention attempts (contact info shared in messages, etc.) for the Large
// Projects module's anti-circumvention enforcement.
//
// Consumes besthand-api `listCircumventionAttempts(userId, limit, offset)`
// (returns `circumventionAttempts`) through the `/lp-graphql` proxy via `lpApi`.
// Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_CIRCUMVENTION = `
  query ($userId: String, $limit: Int, $offset: Int) {
    listCircumventionAttempts(userId: $userId, limit: $limit, offset: $offset) {
      status errorMessage
      circumventionAttempts {
        id userId surface sourceId patternKinds matchesJson
        ipAddress userAgent createdAt
        reviewStatus: status reviewedBy reviewedAt reviewNotes
      }
    }
  }
`;

const M_REVIEW = `
  mutation ($attemptId: Int!, $decision: String!, $notes: String) {
    reviewCircumventionAttempt(attemptId: $attemptId, decision: $decision, notes: $notes) {
      status errorMessage
    }
  }
`;

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  input: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', minWidth: 180 },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  kinds: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 },
  kind: { fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', padding: '3px 9px', borderRadius: 999, background: '#fde7e8', color: '#c2151c' },
  code: { fontFamily: "'SFMono-Regular', Menlo, monospace", fontSize: 12, color: '#5a6168', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8, display: 'block', background: '#fafbfc', borderRadius: 8, padding: '8px 10px', border: '1px solid #eef1f3' },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' },
  btnReview: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnDismiss: { background: '#fff', color: '#5a6168', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  badge: (kind) => {
    const map = {
      OPEN: { bg: '#fff3d6', fg: '#946200' },
      REVIEWED: { bg: '#e7f6ec', fg: '#137a37' },
      DISMISSED: { bg: '#eef2f4', fg: '#5a6168' },
    };
    const c = map[kind] || map.OPEN;
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  reviewMeta: { color: '#6b7280', fontSize: 12, fontWeight: 600 },
};

const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Circumvention() {
  const [items, setItems] = useState([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (userIdArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_CIRCUMVENTION, { userId: userIdArg || null, limit: 200, offset: 0 });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listCircumventionAttempts;
        if (env && env.status === 200) setItems(env.circumventionAttempts || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load circumvention attempts: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(''); }, [load]);

  const review = async (c, decision) => {
    const verb = decision === 'REVIEWED' ? 'Review' : 'Dismiss';
    const notesRaw = typeof window !== 'undefined'
      ? window.prompt(`${verb} attempt #${c.id}? Optional notes:`, '')
      : '';
    if (notesRaw === null) return; // admin cancelled the prompt
    const notes = notesRaw.trim() || null;
    setBusyId(c.id);
    setMsg(null);
    try {
      const res = await lpApi(M_REVIEW, { attemptId: c.id, decision, notes });
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data.reviewCircumventionAttempt;
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: `Attempt #${c.id} marked ${decision.toLowerCase()}.` });
        await load(userId.trim());
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Circumvention Attempts</h2>
      <div style={S.hint}>
        Large Projects — flagged attempts to take a project off-platform (contact info shared in messages, etc.),
        newest first. Filter by user ID.
      </div>

      <div style={S.toolbar}>
        <input style={S.input} placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
        <button style={S.btn} onClick={() => load(userId.trim())} disabled={loading}>Apply</button>
        <button style={S.btnGhost} onClick={() => { setUserId(''); load(''); }} disabled={loading}>Clear</button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{items.length} shown</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        items.length === 0 ? (
          <div style={S.empty}>No circumvention attempts flagged.</div>
        ) : items.map((c) => (
          <div style={S.card} key={c.id}>
            <div style={S.rowTop}>
              <div style={{ width: '100%' }}>
                <p style={S.name}>Attempt #{c.id} · {c.surface || 'unknown surface'}</p>
                <div style={S.meta}>
                  User {c.userId}
                  {c.sourceId ? ` · Source ${c.sourceId}` : ''}
                  {c.createdAt ? ` · ${fmtDate(c.createdAt)}` : ''}
                  {c.ipAddress ? ` · ${c.ipAddress}` : ''}
                </div>
                {(c.patternKinds && c.patternKinds.length) ? (
                  <div style={S.kinds}>
                    {c.patternKinds.map((k, i) => <span key={i} style={S.kind}>{k}</span>)}
                  </div>
                ) : null}
                {c.matchesJson ? <span style={S.code}>{c.matchesJson}</span> : null}
                <div style={S.actions}>
                  {(!c.reviewStatus || c.reviewStatus === 'OPEN') ? (
                    <React.Fragment>
                      <button style={S.btnReview} disabled={busyId === c.id} onClick={() => review(c, 'REVIEWED')}>
                        {busyId === c.id ? '…' : 'Review'}
                      </button>
                      <button style={S.btnDismiss} disabled={busyId === c.id} onClick={() => review(c, 'DISMISSED')}>
                        Dismiss
                      </button>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <span style={S.badge(c.reviewStatus)}>{c.reviewStatus}</span>
                      <span style={S.reviewMeta}>
                        {c.reviewedBy ? `by ${c.reviewedBy}` : ''}
                        {c.reviewedAt ? ` · ${fmtDate(c.reviewedAt)}` : ''}
                        {c.reviewNotes ? ` · “${c.reviewNotes}”` : ''}
                      </span>
                    </React.Fragment>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default Circumvention;
